import React from 'react';
import { Checkbox } from '../ui/checkbox';
import { Slider } from '../ui/slider';
import StarRating from '../StarRating';

const Section = ({ title, children }) => (
    <div className="py-5 border-b border-[rgba(90,36,50,0.1)] last:border-b-0">
        <p className="eyebrow mb-3" style={{ color: 'var(--brand-burgundy)' }}>{title}</p>
        {children}
    </div>
);

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

export default function ShopFilters({ collections = [], sizes = [], colors = [], priceBounds = [0, 0], filters, onChange, format, showRating = true, showCategory = true }) {
    const set = (patch) => onChange({ ...filters, ...patch });
    const [minB, maxB] = priceBounds;

    return (
        <div data-testid="shop-filters">
            {showCategory && (
                <Section title="Category">
                    <div className="space-y-2.5">
                        {collections.map((c) => (
                            <label key={c.slug} className="flex items-center gap-2.5 cursor-pointer text-sm" data-testid={`filter-category-${c.slug}`}>
                                <Checkbox
                                    checked={filters.collections.includes(c.slug)}
                                    onCheckedChange={() => set({ collections: toggle(filters.collections, c.slug) })}
                                />
                                <span style={{ color: 'var(--ink)' }}>{c.name}</span>
                            </label>
                        ))}
                    </div>
                </Section>
            )}

            {maxB > minB && (
                <Section title="Price">
                    <Slider
                        min={minB}
                        max={maxB}
                        step={Math.max(1, Math.round((maxB - minB) / 50))}
                        value={filters.price}
                        onValueChange={(v) => set({ price: v })}
                        data-testid="filter-price-slider"
                        className="mt-1"
                    />
                    <div className="flex justify-between mt-3 text-xs" style={{ color: 'var(--ink-muted)' }}>
                        <span data-testid="filter-price-min">{format(filters.price[0])}</span>
                        <span data-testid="filter-price-max">{format(filters.price[1])}</span>
                    </div>
                </Section>
            )}

            {sizes.length > 0 && (
                <Section title="Size">
                    <div className="flex flex-wrap gap-2">
                        {sizes.map((s) => {
                            const active = filters.sizes.includes(s);
                            return (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => set({ sizes: toggle(filters.sizes, s) })}
                                    data-testid={`filter-size-${s.replace(/\s+/g, '-').toLowerCase()}`}
                                    className="px-3 py-1.5 rounded-lg text-xs border transition-colors"
                                    style={active
                                        ? { backgroundColor: 'var(--brand-burgundy)', color: '#fff', borderColor: 'var(--brand-burgundy)' }
                                        : { backgroundColor: '#fff', color: 'var(--ink)', borderColor: 'rgba(90,36,50,0.18)' }}
                                >
                                    {s}
                                </button>
                            );
                        })}
                    </div>
                </Section>
            )}

            {colors.length > 0 && (
                <Section title="Color">
                    <div className="flex flex-wrap gap-2">
                        {colors.map((c) => {
                            const active = filters.colors.includes(c);
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => set({ colors: toggle(filters.colors, c) })}
                                    data-testid={`filter-color-${c.replace(/\s+/g, '-').toLowerCase()}`}
                                    className="px-3 py-1.5 rounded-lg text-xs border transition-colors"
                                    style={active
                                        ? { backgroundColor: 'var(--brand-burgundy)', color: '#fff', borderColor: 'var(--brand-burgundy)' }
                                        : { backgroundColor: '#fff', color: 'var(--ink)', borderColor: 'rgba(90,36,50,0.18)' }}
                                >
                                    {c}
                                </button>
                            );
                        })}
                    </div>
                </Section>
            )}

            {showRating && (
                <Section title="Rating">
                    <div className="flex items-center gap-3">
                        <StarRating value={filters.minRating} onChange={(v) => set({ minRating: v === filters.minRating ? 0 : v })} size={22} testId="filter-rating" />
                        <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{filters.minRating > 0 ? `${filters.minRating}★ & up` : 'Any'}</span>
                    </div>
                </Section>
            )}

            <Section title="Availability">
                <label className="flex items-center gap-2.5 cursor-pointer text-sm" data-testid="filter-instock">
                    <Checkbox checked={filters.inStock} onCheckedChange={(v) => set({ inStock: !!v })} />
                    <span style={{ color: 'var(--ink)' }}>In stock only</span>
                </label>
            </Section>
        </div>
    );
}
